import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Minus, ShoppingCart, Loader } from 'lucide-react';
import { ordersApi, MenuItem } from '../services/ordersApi';
import { MenuFavoriteButton } from '../components/MenuFavoriteButton';
import { useCart } from '../context/CartContext';
import { operatingStatusQueryOptions } from '../queries/operatingStatus';

export default function ProductScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { add, increment, decrement, items } = useCart();
  const [dish, setDish] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(true);
  const { data: operatingStatus } = useQuery(operatingStatusQueryOptions());
  const scheduleClosed = operatingStatus && !operatingStatus.isOpen;

  useEffect(() => {
    if (id) {
      ordersApi.getMenuItem(id, 'delivery').then(item => {
        setDish(item);
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <Loader className="animate-spin" size={32} style={{ color: 'rgba(255,255,255,0.35)' }} />
      </div>
    );
  }

  if (!dish) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <p style={{ color: 'rgba(255,255,255,0.4)' }}>Блюдо не найдено</p>
      </div>
    );
  }

  const qty = items.find(i => i.id === dish.id)?.quantity ?? 0;
  const addBlocked = dish.isUnavailable || !!scheduleClosed;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0a', color: '#fff' }}>
      <div className="max-w-md mx-auto w-full flex flex-col flex-1 relative">

        {/* Hero image */}
        <div className="relative">
          {dish.image ? (
            <div className="w-full relative" style={{ paddingTop: '66%' }}>
              <img
                src={dish.image}
                alt={dish.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 35%, rgba(10,10,10,0.85) 100%)' }}
              />
            </div>
          ) : (
            <div className="w-full" style={{ paddingTop: '66%', background: 'rgba(255,255,255,0.06)' }} />
          )}

          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-90"
            style={{
              background: 'rgba(10,10,10,0.7)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <ArrowLeft size={20} style={{ color: '#fff' }} />
          </button>
          <div
            className="absolute top-4 right-4 w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{
              background: 'rgba(10,10,10,0.7)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            <MenuFavoriteButton itemId={dish.id} size={18} className="w-full h-full" />
          </div>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex-1 px-4 pt-5 pb-28"
        >
          <div className="flex items-start justify-between mb-4">
            <h1
              className="text-2xl font-bold leading-tight flex-1 mr-3 uppercase"
              style={{ letterSpacing: '-0.03em', fontFamily: 'Unbounded, sans-serif' }}
            >
              {dish.name}
            </h1>
            <span className="text-2xl font-bold shrink-0">
              {dish.price} ₽
            </span>
          </div>

          {dish.nutrition && (
            <div
              className="grid grid-cols-4 gap-2 p-4 mb-4 rounded-2xl"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {[
                { label: 'Ккал', value: dish.nutrition.energy },
                { label: 'Белки', value: `${dish.nutrition.proteins}г` },
                { label: 'Жиры', value: `${dish.nutrition.fats}г` },
                { label: 'Углев.', value: `${dish.nutrition.carbs}г` },
              ].map(item => (
                <div key={item.label} className="text-center">
                  <div className="text-base font-bold">{item.value}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{item.label}</div>
                </div>
              ))}
            </div>
          )}

          {dish.description && (
            <div
              className="rounded-2xl p-4 mb-5"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <h3
                className="text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
              >
                О блюде
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{dish.description}</p>
            </div>
          )}

          <div className="mb-6">
            {qty === 0 ? (
              <button
                onClick={() => add({ id: dish.id, name: dish.name, price: dish.price, quantity: 1, source: 'api' as const, ...(dish.image ? { image: dish.image } : {}) })}
                disabled={addBlocked}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-base transition-all active:scale-95 disabled:opacity-50 tracking-[0.08em] uppercase"
                style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
              >
                <ShoppingCart size={18} />
                {dish.isUnavailable ? 'Недоступно' : scheduleClosed ? 'Закрыто' : 'В корзину'}
              </button>
            ) : (
              <div
                className="flex items-center justify-between rounded-2xl px-4 py-3"
                style={{ background: '#fff' }}
              >
                <button
                  onClick={() => decrement(dish.id)}
                  className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-transform"
                  style={{ background: 'rgba(0,0,0,0.08)' }}
                >
                  <Minus size={16} style={{ color: '#0a0a0a' }} />
                </button>
                <span className="font-bold text-lg" style={{ color: '#0a0a0a' }}>{qty}</span>
                <button
                  onClick={() => increment(dish.id)}
                  disabled={addBlocked}
                  className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40"
                  style={{ background: 'rgba(0,0,0,0.08)' }}
                >
                  <Plus size={16} style={{ color: '#0a0a0a' }} />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
